import React, {useEffect, useState} from 'react'
import CardBody from '../../../components/Card/CardBody';
import Card from '../../../components/Card/Card';
import {useDispatch, useSelector} from 'react-redux';
import {
  alertActions,
  categoryActions,
  classificationActions,
  companyActions,
  documentActions,
  sectionActions,
  vodActions
} from '../../../store/actions';

import {UploadListPanel} from './components';
import PageHeader from "../../../components/PageHeader/PageHeader";
import {makeStyles} from "@mui/styles";
import LoadingSpinner from "../../../components/Spinner/loading.spinner";
import VodProgressView from "./components/VodProgressDialog/VodProgressView";
import {AccordionCard} from "../components";
import {GuidBasedMenuItem} from "../components/panelSegments/components";
import UploadFilterButtons from "./components/UploadFilterButtons/UploadFilterButtons";
import {
  NameBasedMenuItemWithIdReturn
} from "../components/panelSegments/components/NameBasedMenuItem";
import {OfficeDocTypes} from "../../../constants/filetypes/OfficeDocTypes";
import {DrawingDocTypes} from "../../../constants/filetypes/DrawingDocTypes";
import {AudioVideoDocTypes} from "../../../constants/filetypes/AudioVideoDocTypes";
import UploadDocTypeList from "../../../constants/filetypes/ValidDocTypesForUploadWindow";
import TabList from "../../../components/Tab/TabList";
import FileListTabMode from "../components/FileListWindow/FileListTabMode";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
  content: {
    margin: 0,
    padding: 0,
    backgroundColor: '#FFF'
  },
  button: {
    margin: theme.spacing(2),
    marginTop: 0
  },
  buttonError: {
    margin: theme.spacing(2),
    marginTop: 0
  }
}));

/**
 *
 * @param props {{type: ("document" | "image" | "drawing/CAD" | "video" | "audio")[] }}
 * @return {JSX.Element}
 * @constructor
 */
const UploadDocument = props => {
  const {type} = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const companies = useSelector(state => state.companies.items);
  const categories = useSelector(state => state.categories.items);
  const classifications = useSelector(state => state.classifications.items);
  const sections = useSelector(state => state.sections.items);
  const company = useSelector(state => state.authentication.user.company);
  const companyId = useSelector(state => state.authentication.user.companyId);
  const user = useSelector(state => state.authentication.user.id);
  const role = useSelector(state => state.authentication.user.role);
  const authSessionExpirationTime = useSelector(state => state.authentication.user.exp);
  const requestPending = useSelector(state => state.documents.requestPending || state.vod.requestPending);
  const vodRequestPending = useSelector(state => state.vod.requestPending);

  const [fileList, setFileList] = useState([]);
  const [companySelect, setCompanySelect] = useState(companyId);
  const [sectionSelect, setSectionSelect] = useState(1);
  const [categorySelect, setCategorySelect] = useState(13);
  const [shareable, setShareable] = useState(true);
  const [checkedB] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const {uploadEnabled, uploadDisabledReason} = useSelector(state => {
    if (!state)
      return {uploadEnabled: false, uploadDisabledReason: "No State"};

    if (!state.companies || !state.companies.items || state.companies.items.length === 0)
      return {uploadEnabled: false, uploadDisabledReason: "No Companies"};

    if (!state.sections || !state.sections.items || state.sections.items.length === 0)
      return {uploadEnabled: false, uploadDisabledReason: "No Sections"};

    if (!state.categories || !state.categories.items || state.categories.items.length === 0)
      return {uploadEnabled: false, uploadDisabledReason: "No Categories"};

    return {uploadEnabled: true, uploadDisabledReason: ""}
  });

  const mimeTypeList = type.map(typeVal => UploadDocTypeList[typeVal]).flat()

  const onHandleReset = () => {
    setFileList([]);
  };

  let formData = new FormData();

  const onHandleChange = event => {
    const files = event.target.files;

    if (!files || !files.length || files.length === 0){
      setFileList([]);
      return;
    }

    let errorMsg = "";
    let badFileNum = 0;
    let validFiles = [];
    let currentFile;

    for (let index = 0; index < files.length; index++){
      currentFile = files[index];
      if (!currentFile || !currentFile.size || currentFile.size === 0){
        badFileNum++;
        errorMsg = `${errorMsg}\n${currentFile.name}`;
      } else {
        validFiles.push(currentFile);
      }
    }

    if (badFileNum > 0){
      errorMsg = `The following ${badFileNum} files had no contents${errorMsg}`;
      dispatch(alertActions.error(<p style={{whiteSpace: "pre-line"}}>{errorMsg}</p>));
    }

    setFileList(validFiles);
  };

  const onHandleUpload = async () => {
    formData = new FormData();
    formData.append('company', companySelect);
    formData.append('user', user);
    formData.append('section', sectionSelect);
    formData.append('category', categorySelect);
    formData.append('shareable', shareable);

    let filePromises = [];
    const nowSeconds = (new Date()).valueOf() / 1000;
    if (authSessionExpirationTime - nowSeconds < 30) {
      setFileList([]);

      const errorMsg = "Session too close to expiration to upload. Log back in to upload files.";
      dispatch(alertActions.error(errorMsg));
      return new Error(errorMsg);
    }

    for (let i = 0; i < fileList.length; i++) {
      const contentType =
        AudioVideoDocTypes.isDocType(fileList[i]) ??
        OfficeDocTypes.isDocType(fileList[i]) ??
        DrawingDocTypes.isDocType(fileList[i])
      if (contentType) {
        uploadDocToFileConversionApi(fileList[i], contentType, filePromises);
      } else {
        formData.append('fileList[]', fileList[i]);
      }
    }
    setFileList([]);
    await Promise.all(filePromises).then(() => {/*Nothing*/});
    if (formData.getAll('fileList[]').length > 0 || formData.getAll('fileLinks[]').length > 0)
      dispatch(documentActions.uploadDocuments(formData));
  };

  function uploadDocToFileConversionApi(file, contentType, filePromises) {
    let videoFormData = new FormData();
    let fileWithNewContentType = new Blob([file], {type: contentType})
    videoFormData.append("file", fileWithNewContentType, file.name);
    let uploadPromise = Promise.resolve(dispatch(vodActions.uploadToVoDApi(sectionSelect, videoFormData))
      .then(
        avUploadResolveFunction,
        rej => new Error(rej)
      ));
    filePromises.push(uploadPromise)
    uploadTabRef.current.addTab(
      (id) => file.name,
      (id) => FileListTabMode.PROGRESS,
      (id) => (<VodProgressView
      progressPromise={uploadPromise}
    />));
  }

  function avUploadResolveFunction(res) {
    if (!res) {
      return new Error("Resolved as if query succeeded, but no value");
    }
    formData.append('fileLinks[]', JSON.stringify(res));
    return res;
  }

  const onHandleSectionSelect = event => {
    setSectionSelect(event.target.value);
  };

  const onHandleCompanySelect = event => {
    setCompanySelect(event.target.value);
  };

  const onHandleCategorySelect = event => {
    setCategorySelect(event.target.value);
  };

  const onHandleShareableFlag = event => {
    if (event.target.checked){
        setShareable(true);
    } else {
        setShareable(false);
    }
  };

  useEffect(() => {
    dispatch(companyActions.getAllActiveCompaniesForUpload());
    dispatch(categoryActions.getAllCategories());
    dispatch(sectionActions.getAllSections());
    dispatch(classificationActions.getAllClassifications());
  }, [dispatch]);

  const defaultTab = (!user || !companies || !categories || !classifications || !sections || requestPending || vodRequestPending) ?
    (<LoadingSpinner/>) :
    (<React.Fragment>
      <AccordionCard
        classes={classes}
        role={role}
        expanded={expanded}
        setExpanded={setExpanded}

        initialSections={sections}
        initialCompanies={companies}
        initialCategories={categories}

        sectionSelect={sectionSelect}
        companySelect={companySelect}
        categorySelect={categorySelect}
        shareableFlag={shareable}

        onHandleSectionSelect={onHandleSectionSelect}
        onHandleCompanySelect={onHandleCompanySelect}
        onHandleCategorySelect={onHandleCategorySelect}
        onShareableChange={onHandleShareableFlag}

        searchSectionsByMethod={NameBasedMenuItemWithIdReturn}
        searchCategoriesByMethod={NameBasedMenuItemWithIdReturn}
        searchCompaniesByMethod={GuidBasedMenuItem}

        renderStatus={false}
        renderClassification={false}
        renderBeginDate={false}
        renderEndDate={false}
        renderFilename={false}
        renderAttributes={false}
        renderShareable={true}
        renderNbrOfItemsToAdd={false}

        includeAllStatus={false}
        includeAllCategories={false}
        includeAllClassifications={false}
        includeAllCompanies={false}
        includeAllSections={false}

        browseFilter={true}
        summaryText={type}

        renderButton={false}
        customButtons={UploadFilterButtons({
          classes,
          mimeTypeList,
          fileList,
          onHandleChange,
          onHandleUpload,
          onHandleReset,
          enabled: uploadEnabled,
          disabledMessage: uploadDisabledReason
        })}
      />
        {!fileList ? (<LoadingSpinner/>) : (
          <UploadListPanel
            checkPin={checkedB}
            classes={classes}
            fileList={fileList}
          />
        )}
    </React.Fragment>
    );


  const uploadTabRef = React.useRef({});

  return (
    <div className={classes.root}>
      <PageHeader title={"Upload"} subTitle={type.join(", ")}/>
      <Card
        plain
        style={{margin: 0}}
      >
        <CardBody style={{backgroundColor: '#FFF', padding: 0}}>
          <TabList
            label="Progress Tabs"
            indicatorColor="secondary"
            scrollButtons="auto"
            textColor="primary"
            variant="standard"
            defaultTabName="Upload"
            defaultTabPanel={defaultTab}
            ref={uploadTabRef}
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default UploadDocument;
